import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./VendorIntegrationDetails.module.scss";
import VendorIntegrationService from "@/services/api/vendorIntegration";
import { capitalizeText, combineClasses, dateConverter } from "@/lib/utils";
import LoadingText from "@/components/LoadingText/LoadingText";
import Button from "@/components/Button/Button";
import { MODAL_ID } from "@/lib/constants";
import { setModalOpen } from "@/store/slices/globalSlice";
import AddApiEndpoint from "./components/AddApiEndpoint/AddApiEndpoint";
import ActionsCell from "@/components/ActionCells/ActionsCell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import toast from "@/lib/toast";

const VendorIntegrationDetails = () => {
  const { vendorIntegrationId } = useParams();
  const dispatch = useDispatch();
  const { modalOpen } = useSelector((state) => state.global);
  const [vendorIntegration, setVendorIntegration] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [apiEndpoints, setApiEndpoints] = useState([]);
  const [isFetchingEndpoints, setIsFetchingEndpoints] = useState(false);

  const getData = useCallback(async () => {
    setIsFetching(true);
    const data = await VendorIntegrationService.getVendorIntegrationById(
      vendorIntegrationId
    );
    setVendorIntegration(data?.data);
    setIsFetching(false);
  }, [vendorIntegrationId]);

  const getApiEndpoints = useCallback(async () => {
    setIsFetchingEndpoints(true);
    const data = await VendorIntegrationService.getApiEndpoints({
      vendorIntegrationId,
    });
    const rows = data?.data?.rows || data?.data || [];
    setApiEndpoints(Array.isArray(rows) ? rows : []);
    setIsFetchingEndpoints(false);
  }, [vendorIntegrationId]);

  const refreshData = useCallback(async () => {
    await Promise.all([getData(), getApiEndpoints()]);
  }, [getData, getApiEndpoints]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const vendor = vendorIntegration?.vendor;

  if (isFetching) {
    return <LoadingText text="Loading" />;
  }

  if (!vendorIntegration) {
    return <p className={styles.noData}>No vendor details found.</p>;
  }

  const openAddApiEndpointModal = () => {
    dispatch(
      setModalOpen({
        id: MODAL_ID.ADD_API_ENDPOINT,
        data: { vendorIntegrationId, vendorId: vendor?.id },
      })
    );
  };

  const openDeleteApiEndpointModal = (item) => {
    dispatch(
      setModalOpen({
        id: MODAL_ID.CONFIRM_DELETE,
        data: { id: item?.id, userName: item?.endpoint },
      })
    );
  };

  const handleDeleteApiEndpoint = async () => {
    const id = modalOpen?.data?.id;
    const data = await VendorIntegrationService.deleteApiEndpoint(
      id,
      vendorIntegrationId
    );
    if (data) {
      toast.success(data?.message || "API endpoint deleted successfully");
      dispatch(setModalOpen(false));
      getApiEndpoints();
    }
  };

  return (
    <div className={styles.main}>
      <AddApiEndpoint getData={refreshData} />
      <ConfirmDeleteModal
        deleteLabel="API endpoint"
        handleDelete={handleDeleteApiEndpoint}
      />
      <div className={styles.actionsContainer}>
        <Button className={styles.addEndpointButton} onClick={openAddApiEndpointModal}>
          Add Endpoint
        </Button>
      </div>
      <div className={styles.vendorDetails}>
        <div className={styles.cardHeader}>
          <div className={styles.headerInfo}>
            <span className={styles.headerLabel}>Organization</span>
            <h2 className={styles.headerTitle}>
              {vendor?.organizationName || "-"}
            </h2>
          </div>
          <span
            className={combineClasses(
              styles.statusBadge,
              vendor?.status === "approved" ? styles.statusApproved : styles.statusPending
            )}
          >
            {capitalizeText(vendor?.status) || "-"}
          </span>
        </div>

        <div className={styles.cardSection}>
          <span className={styles.sectionTitle}>Integration Details</span>
          <div className={styles.detailsGrid}>
            <div className={styles.vendorItem}>
              <span className={styles.label}>API Key</span>
              <span className={styles.value}>{vendorIntegration.apiKey || "-"}</span>
            </div>

            <div className={styles.vendorItem}>
              <span className={styles.label}>User Name</span>
              <span className={styles.value}>{vendorIntegration.userName || "-"}</span>
            </div>

            <div className={styles.vendorItem}>
              <span className={styles.label}>Email</span>
              <span className={styles.value}>{vendorIntegration.email || "-"}</span>
            </div>

            <div className={styles.vendorItem}>
              <span className={styles.label}>Mobile Number</span>
              <span className={styles.value}>{vendor?.mobileNumber || "-"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.endpointsSection}>
        <div className={styles.endpointsHeader}>
          <h3 className={styles.endpointsTitle}>API Endpoints</h3>
        </div>

        {isFetchingEndpoints ? (
          <LoadingText text="Loading" />
        ) : apiEndpoints.length === 0 ? (
          <p className={styles.noData}>No API endpoints added yet.</p>
        ) : (
          <div className={styles.endpointsList}>
            {apiEndpoints.map((item) => (
              <div key={item.id} className={styles.endpointCard}>
                <div className={styles.endpointCardHeader}>
                  <span
                    className={combineClasses(
                      styles.methodBadge,
                      styles[`method${(item?.method || "").toUpperCase()}`]
                    )}
                  >
                    {item?.method || "-"}
                  </span>
                  <span className={styles.endpointUrl}>{item?.endpoint || "-"}</span>
                  <ActionsCell
                    id={item?.id?.toString()}
                    onDelete={() => openDeleteApiEndpointModal(item)}
                  />
                </div>

                <div className={styles.endpointDetailsGrid}>
                  <div className={styles.vendorItem}>
                    <span className={styles.label}>Invoke On</span>
                    <span className={styles.value}>
                      {capitalizeText(
                        item?.invokeOn?.replaceAll("_", " ").toLowerCase()
                      ) || "-"}
                    </span>
                  </div>

                  {/* <div className={styles.vendorItem}>
                    <span className={styles.label}>Created At</span>
                    <span className={styles.value}>
                      {item?.createdAt ? dateConverter(item.createdAt) : "-"}
                    </span>
                  </div> */}

                  {/* <div className={combineClasses(styles.vendorItem, styles.fullWidth)}>
                    <span className={styles.label}>Auth Key</span>
                    <span className={styles.value} title={item?.authKey}>
                      {item?.authKey
                        ? `${item.authKey.slice(0, 16)}••••••••${item.authKey.slice(-8)}`
                        : "-"}
                    </span>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorIntegrationDetails;
